import { useState, useEffect, useCallback, useRef } from "react";
import {
  Stack,
  Title,
  Group,
  Button,
  TextInput,
  SegmentedControl,
  SimpleGrid,
  Paper,
  Image as MantineImage,
  Text,
  Badge,
  ActionIcon,
  Modal,
  Select,
  Pagination,
  Drawer,
  Loader,
  Center,
  Box,
  ThemeIcon,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import {
  IconSearch,
  IconUpload,
  IconEdit,
  IconPhoto,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { Stage, Layer, Rect, Text as KonvaText, Image as KonvaImage } from "react-konva";
import useImage from "use-image";
import { notifications } from "@mantine/notifications";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTIONS = [
  { label: "Бүгд", value: "" },
  { label: "Шинэ", value: "new" },
  { label: "Тэмдэглэсэн", value: "edited" },
  { label: "Татгалзсан", value: "denied" },
];

const DETECT_COLORS = [
  "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4",
  "#FFEAA7", "#DDA0DD", "#98D8C8", "#F7DC6F",
];

interface Detection {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
}

function AnnotationCanvas({
  imageUrl,
  detections,
  onAdd,
  onRemove,
  canvasWidth,
}: {
  imageUrl: string;
  detections: Detection[];
  onAdd: (rect: { x: number; y: number; width: number; height: number }) => void;
  onRemove: (id: string) => void;
  canvasWidth: number;
}) {
  const [image] = useImage(imageUrl, "anonymous");
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState<any>(null);

  if (!image) return <Center h={400}><Loader /></Center>;

  const scale = canvasWidth / image.width;
  const canvasHeight = image.height * scale;

  const handleMouseDown = (e: any) => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    setDrawing(true);
    setStartPos({ x: pos.x, y: pos.y });
    setCurrentRect(null);
  };

  const handleMouseMove = (e: any) => {
    if (!drawing) return;
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    setCurrentRect({
      x: Math.min(startPos.x, pos.x),
      y: Math.min(startPos.y, pos.y),
      width: Math.abs(pos.x - startPos.x),
      height: Math.abs(pos.y - startPos.y),
    });
  };

  const handleMouseUp = () => {
    setDrawing(false);
    if (currentRect && currentRect.width > 10 && currentRect.height > 10) {
      onAdd({
        x: currentRect.x / scale,
        y: currentRect.y / scale,
        width: currentRect.width / scale,
        height: currentRect.height / scale,
      });
    }
    setCurrentRect(null);
  };

  return (
    <Stage
      width={canvasWidth}
      height={canvasHeight}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{ cursor: "crosshair", borderRadius: 8, overflow: "hidden" }}
    >
      <Layer>
        <KonvaImage image={image} width={canvasWidth} height={canvasHeight} />
        {detections.map((d) => (
          <Rect
            key={d.id}
            x={d.x * scale}
            y={d.y * scale}
            width={d.width * scale}
            height={d.height * scale}
            stroke={d.color}
            strokeWidth={2}
            onDblClick={() => onRemove(d.id)}
          />
        ))}
        {detections.map((d) => (
          <KonvaText
            key={`label-${d.id}`}
            x={d.x * scale}
            y={d.y * scale - 16}
            text={d.name}
            fontSize={12}
            fill={d.color}
            fontStyle="bold"
          />
        ))}
        {currentRect && (
          <Rect
            x={currentRect.x}
            y={currentRect.y}
            width={currentRect.width}
            height={currentRect.height}
            stroke="#00ff00"
            strokeWidth={2}
            dash={[4, 4]}
          />
        )}
      </Layer>
    </Stage>
  );
}

export default function ImageList() {
  const { hasRole } = useAuth();
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState("");

  const [detectOpen, setDetectOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [detectName, setDetectName] = useState("PLATE");
  const [saving, setSaving] = useState(false);

  // Project classnames
  const [classnames, setClassnames] = useState<string[]>([]);
  // Project image types
  const [imageTypes, setImageTypes] = useState<{ name: string; label: string }[]>([]);

  const searchTimeout = useRef<any>(null);

  useEffect(() => {
    api.name.list().then((res) => {
      const names = res.data.map((n: any) => n.name);
      setClassnames(names);
      if (names.length > 0) setDetectName(names[0]);
    }).catch(() => {});

    api.imageType.list().then((res) => {
      setImageTypes(res.data);
      if (res.data.length > 0) setUploadType(res.data[0].name);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.image.list({
        page,
        limit: 20,
        imageStatus: statusFilter || undefined,
        search: search || undefined,
      });
      setImages(res.data);
      setTotalPages(res.pagination.totalPages);
      setTotal(res.pagination.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearchChange = (val: string) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 300);
  };

  const handleUpload = async (files: File[]) => {
    setUploading(true);
    try {
      for (const file of files) {
        await api.image.upload(uploadType, file);
      }
      notifications.show({
        title: "Амжилттай",
        message: `${files.length} зураг хуулагдлаа`,
        color: "green",
      });
      setUploadOpen(false);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally {
      setUploading(false);
    }
  };

  const openDetect = (img: any) => {
    setSelectedImage(img);
    // Populate хийгдсэн detects-ийг ачаалах
    const existing: Detection[] = (img.detects || [])
      .filter((d: any) => d && d._id)
      .map((d: any, i: number) => ({
        id: d._id,
        name: d.name,
        x: d.x,
        y: d.y,
        width: d.width,
        height: d.height,
        color: DETECT_COLORS[i % DETECT_COLORS.length],
      }));
    setDetections(existing);
    if (classnames.length > 0) setDetectName(classnames[0]);
    setDetectOpen(true);
  };

  const addDetection = (rect: { x: number; y: number; width: number; height: number }) => {
    const newDetection: Detection = {
      id: `${Date.now()}-${Math.random()}`,
      name: detectName,
      ...rect,
      color: DETECT_COLORS[detections.length % DETECT_COLORS.length],
    };
    setDetections((prev) => [...prev, newDetection]);
  };

  const removeDetection = (id: string) => {
    setDetections((prev) => prev.filter((d) => d.id !== id));
  };

  const updateDetectionName = (id: string, newName: string) => {
    setDetections((prev) =>
      prev.map((d) => (d.id === id ? { ...d, name: newName } : d))
    );
  };

  const saveDetections = async () => {
    if (!selectedImage || detections.length === 0) return;
    setSaving(true);
    try {
      await api.image.detect({
        imageId: selectedImage._id,
        width: selectedImage.width,
        height: selectedImage.height,
        detections: detections.map((d) => ({
          name: d.name,
          x: d.x,
          y: d.y,
          width: d.width,
          height: d.height,
        })),
      });
      notifications.show({
        title: "Амжилттай",
        message: "Тэмдэглэгээ хадгалагдлаа",
        color: "green",
      });
      setDetectOpen(false);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const statusColor = (s: string) => {
    if (s === "edited") return "green";
    if (s === "denied") return "red";
    return "gray";
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>Зурагнууд</Title>
          <Badge variant="light" size="lg">{total}</Badge>
        </Group>
        {hasRole("admin", "maintainer") && (
          <Button
            leftSection={<IconUpload size={16} />}
            onClick={() => setUploadOpen(true)}
            size="sm"
          >
            Зураг хуулах
          </Button>
        )}
      </Group>

      <Group>
        <TextInput
          placeholder="Файлын нэрээр хайх..."
          leftSection={<IconSearch size={16} />}
          onChange={(e) => handleSearchChange(e.currentTarget.value)}
          w={300}
          size="sm"
        />
        <SegmentedControl
          data={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(val) => {
            setStatusFilter(val);
            setPage(1);
          }}
          size="sm"
        />
      </Group>

      {loading ? (
        <Center h={300}>
          <Loader />
        </Center>
      ) : images.length === 0 ? (
        <Center h={300}>
          <Stack align="center" gap="xs">
            <ThemeIcon size={48} variant="light" color="gray" radius="xl">
              <IconPhoto size={24} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">
              Зураг олдсонгүй
            </Text>
          </Stack>
        </Center>
      ) : (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }}>
            {images.map((img) => (
              <Paper
                key={img._id}
                withBorder
                radius="md"
                style={{
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "box-shadow 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <Box pos="relative">
                  <MantineImage
                    src={`/uploads/${img.filepath}`}
                    h={150}
                    fit="cover"
                    fallbackSrc="https://placehold.co/300x200?text=No+Image"
                  />
                  <Badge
                    pos="absolute"
                    top={8}
                    right={8}
                    color={statusColor(img.imageStatus)}
                    size="xs"
                    variant="filled"
                  >
                    {img.imageStatus}
                  </Badge>
                </Box>
                <Stack gap={2} p="xs">
                  <Group justify="space-between" wrap="nowrap" gap={4}>
                    <Text size="xs" truncate style={{ flex: 1 }} c="dimmed">
                      {img.description || img.filename}
                    </Text>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="blue"
                      onClick={() => openDetect(img)}
                    >
                      <IconEdit size={14} />
                    </ActionIcon>
                  </Group>
                  <Group gap={4} wrap="wrap">
                    {img.keys?.map((k: string) => (
                      <Badge key={k} size="xs" variant="light" color={k === "CAN" ? "orange" : "green"}>
                        {k}
                      </Badge>
                    ))}
                    <Text size="10px" c="dimmed">
                      {new Date(img.createdAt).toLocaleString("mn-MN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </Text>
                  </Group>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>

          {totalPages > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          )}
        </>
      )}

      {/* Upload Drawer */}
      <Drawer
        opened={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title={<Text fw={600}>Зураг хуулах</Text>}
        position="right"
        size="md"
      >
        <Stack>
          <Select
            label="Зургийн төрөл"
            size="sm"
            data={imageTypes.map((t) => ({ value: t.name, label: t.label }))}
            value={uploadType}
            onChange={(v) => setUploadType(v || "")}
            placeholder="Төрөл сонгох"
          />
          <Dropzone
            onDrop={handleUpload}
            accept={IMAGE_MIME_TYPE}
            loading={uploading}
            multiple
            radius="md"
          >
            <Group justify="center" gap="xl" mih={200} style={{ pointerEvents: "none" }}>
              <Dropzone.Accept>
                <IconUpload size={50} stroke={1.5} color="var(--mantine-color-blue-6)" />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={50} stroke={1.5} color="var(--mantine-color-red-6)" />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto size={50} stroke={1.5} color="var(--mantine-color-dimmed)" />
              </Dropzone.Idle>
              <div>
                <Text size="md" inline fw={500}>
                  Зураг чирж оруулах эсвэл дарж сонгох
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  JPEG, PNG, WebP — 20MB хүртэл
                </Text>
              </div>
            </Group>
          </Dropzone>
        </Stack>
      </Drawer>

      {/* Detect Modal */}
      <Modal
        opened={detectOpen}
        onClose={() => setDetectOpen(false)}
        title={<Text fw={600}>Бүс тэмдэглэх</Text>}
        size="90%"
        fullScreen
      >
        {selectedImage && (
          <Stack>
            <Group align="flex-start" wrap="nowrap">
              <Box style={{ flex: 1, overflow: "auto" }}>
                <AnnotationCanvas
                  imageUrl={`/uploads/${selectedImage.filepath}`}
                  detections={detections}
                  onAdd={addDetection}
                  onRemove={removeDetection}
                  canvasWidth={Math.min(900, window.innerWidth - 350)}
                />
              </Box>
              <Paper withBorder p="sm" w={280} radius="md">
                <Stack gap={4} mb="sm" pb="sm" style={{ borderBottom: "1px solid #eee" }}>
                  <Text fw={600} size="sm">Мэдээлэл</Text>
                  <Group gap={6}>
                    <Text size="xs" c="dimmed">Файл:</Text>
                    <Text size="xs" truncate style={{ flex: 1 }}>
                      {selectedImage.filename}
                    </Text>
                  </Group>
                  {selectedImage.description && (
                    <Group gap={6}>
                      <Text size="xs" c="dimmed">Тайлбар:</Text>
                      <Text size="xs" truncate style={{ flex: 1 }}>
                        {selectedImage.description}
                      </Text>
                    </Group>
                  )}
                  <Group gap={6}>
                    <Text size="xs" c="dimmed">Огноо:</Text>
                    <Text size="xs">
                      {new Date(selectedImage.createdAt).toLocaleString("mn-MN")}
                    </Text>
                  </Group>
                  {selectedImage.machineId && (
                    <Group gap={6}>
                      <Text size="xs" c="dimmed">Machine:</Text>
                      <Text size="xs" ff="monospace" truncate style={{ flex: 1 }}>
                        {selectedImage.machineId}
                      </Text>
                    </Group>
                  )}
                  {selectedImage.keys && selectedImage.keys.length > 0 && (
                    <Group gap={4} wrap="wrap">
                      <Text size="xs" c="dimmed">Таньсан:</Text>
                      {selectedImage.keys.map((k: string) => (
                        <Badge
                          key={k}
                          size="xs"
                          variant="light"
                          color={k === "CAN" ? "orange" : "green"}
                        >
                          {k}
                        </Badge>
                      ))}
                    </Group>
                  )}
                  <Group gap={6}>
                    <Text size="xs" c="dimmed">Хэмжээ:</Text>
                    <Text size="xs">
                      {selectedImage.width}×{selectedImage.height}
                    </Text>
                  </Group>
                </Stack>
                <Text fw={600} size="sm" mb="sm">
                  Тэмдэглэлүүд ({detections.length})
                </Text>
                <Stack gap="xs">
                  {detections.map((d) => (
                    <Group key={d.id} justify="space-between" wrap="nowrap" gap={4}>
                      <Box w={8} h={8} style={{ borderRadius: "50%", backgroundColor: d.color, flexShrink: 0 }} />
                      <Select
                        size="xs"
                        data={classnames.length > 0 ? classnames : ["PLATE"]}
                        value={d.name}
                        onChange={(v) => v && updateDetectionName(d.id, v)}
                        style={{ flex: 1 }}
                        styles={{ input: { fontSize: "11px", minHeight: "24px", height: "24px" } }}
                      />
                      <ActionIcon
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={() => removeDetection(d.id)}
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Group>
                  ))}
                  {detections.length === 0 && (
                    <Text size="xs" c="dimmed" ta="center" py="md">
                      Зураг дээр чирж бүс зурна
                    </Text>
                  )}
                </Stack>
                <Button
                  fullWidth
                  mt="md"
                  onClick={saveDetections}
                  loading={saving}
                  disabled={detections.length === 0}
                  size="sm"
                >
                  Хадгалах
                </Button>
              </Paper>
            </Group>

            <Text size="xs" c="dimmed">
              Зураг дээр чирж бүс зурна. Хос товшиж устгана.
            </Text>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
